import { offerAtom } from "@core/offers/atoms";
import { OfferStatus } from "@core/offers/types";
import { type BreadcrumbItem } from "@core/shared/types";
import { money, URLS } from "@core/shared/utils";
import { trans } from "@mongez/localization";
import { useNavigate } from "@remix-run/react";
import { useMemo } from "react";
import { useIsLoggedIn } from "../../../framework/remix/account/hooks";
import { useAction } from "../../../framework/remix/shared/hooks";
import { paymentAtom, paymentPortal } from "../../checkout/atoms";
import { errorToast, successToast } from "../../design-system/components/toast";

// Offer Details Hook
export function useOfferDetails() {
  const offer = offerAtom.useValue();

  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    return [
      {
        text: trans("offers.title"),
        url: URLS.offers.list,
      },
      {
        text: offer.name,
        url: "",
      },
    ];
  }, [offer]);

  return {
    breadcrumbs,
  };
}

// Offer Header Hook
export function useOfferHeader() {
  const offer = offerAtom.useValue();

  const {
    name,
    description,
    image,
    price,
    oldPrice,
    salePercentage,
    lessons,
    startsAt,
    endsAt,
    isFree,
  } = offer;

  // Check if offer is free (either isFree flag is true or price is 0)
  const isFreeOffer = isFree || price === 0;

  const priceText = isFreeOffer ? trans("free") : money(price);
  const priceSubtext = isFreeOffer
    ? trans("noCost")
    : trans("offers.oneTimePayment");

  return {
    offer,
    name,
    description,
    image,
    price,
    oldPrice,
    salePercentage,
    lessons,
    startsAt,
    endsAt,
    priceText,
    priceSubtext,
  };
}

// Offer Quick Actions Hook
export function useOfferQuickActions() {
  const offer = offerAtom.value;

  // Check if offer is free (either isFree flag is true or price is 0)
  const isFree = offer.isFree || offer.price === 0;

  const buttonText = isFree
    ? trans("offers.unlockForFree")
    : trans("offers.purchaseOffer");
  const priceText = isFree ? trans("free") : money(offer.price);
  const priceSubtext = isFree
    ? trans("noCost")
    : trans("offers.oneTimePayment");

  const handleStartLearning = () => {
    // Scroll to the content tab
    const contentTab = document.querySelector("#offer-content");
    if (contentTab) {
      contentTab.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return {
    offer,
    buttonText,
    priceText,
    priceSubtext,
    handleStartLearning,
    isFree,
  };
}

// Offer Status Hook
export function useOfferStatus() {
  const offer = offerAtom.useValue();
  const isLoggedIn = useIsLoggedIn();

  const getStatusInfo = () => {
    switch (offer.status) {
      case OfferStatus.Purchased:
        return {
          status: "purchased",
          label: "offers.offerUnlocked",
          color:
            "bg-green-100 text-green-800 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900",
          icon: "CheckCircle",
          bgGradient: "from-green-50/60 to-white",
          canPurchase: false,
          showPrice: false,
        };

      case OfferStatus.Pending:
        return {
          status: "pending",
          label: "offers.paymentUnderReview",
          color:
            "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-900",
          icon: "Clock",
          bgGradient: "from-yellow-50/60 to-white",
          canPurchase: false,
          showPrice: true,
        };

      case OfferStatus.Failed:
        return {
          status: "failed",
          label: "offers.paymentFailed",
          color:
            "bg-red-100 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900",
          icon: "XCircle",
          bgGradient: "from-red-50/60 to-white",
          canPurchase: true,
          showPrice: true,
        };

      case OfferStatus.Expired:
        return {
          status: "expired",
          label: "offers.offerExpired",
          color:
            "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600",
          icon: "XCircle",
          bgGradient: "from-gray-50/60 to-white",
          canPurchase: false,
          showPrice: false,
        };

      case OfferStatus.NotStarted:
        return {
          status: "notStarted",
          label: "offers.offerNotStarted",
          color:
            "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900",
          icon: "Clock",
          bgGradient: "from-blue-50/60 to-white",
          canPurchase: false,
          showPrice: true,
        };

      case OfferStatus.New:
      default:
        return {
          status: "new",
          label: "offers.offerAvailable",
          color: "bg-primary-100 text-primary-800 border-primary-200",
          icon: "Gift",
          bgGradient: "from-primary-50/60 to-white",
          canPurchase: true,
          showPrice: true,
        };
    }
  };

  return {
    status: offer.status,
    isLoggedIn,
    getStatusInfo,
  };
}

export function usePurchaseOffer() {
  const isLoggedIn = useIsLoggedIn();
  const navigate = useNavigate();
  const submit = useAction("/invoices");

  const purchaseOffer = async () => {
    if (!isLoggedIn) {
      navigate(URLS.auth.login);
      return;
    }

    const offer = offerAtom.value;

    // Check if offer is free (either isFree flag is true or price is 0)
    const isFree = offer.isFree || offer.price === 0;

    if (isFree) {
      const data = {
        offers: [offer.id],
        paymentMethod: "free",
      };

      const response = await submit(data);

      if (response.isOK) {
        navigate(0); // refresh to fetch the updated offer
        successToast(trans("offers.offerUnlocked"));
      } else {
        errorToast(response.error);
      }
      return;
    }

    // For paid offers, use the payment portal
    paymentAtom.change("offers", [offer]);
    paymentPortal.open();
  };

  return {
    purchaseOffer,
  };
}

// Offer Lessons Hook
export function useOfferLessons() {
  const offer = offerAtom.useValue();

  const groupedLessons = useMemo(() => {
    if (!offer.lessons || offer.lessons.length === 0) {
      return {};
    }

    // Group lessons by grade
    const grouped: { [gradeName: string]: any[] } = {};

    offer.lessons.forEach((lesson) => {
      const gradeName = lesson.grade?.name || "Unknown Grade";
      if (!grouped[gradeName]) {
        grouped[gradeName] = [];
      }
      grouped[gradeName].push(lesson);
    });

    return grouped;
  }, [offer.lessons]);

  return {
    groupedLessons,
  };
}
